import { Component } from 'react';
import { connect } from 'react-redux';

import Form from 'components/atoms/Form';
import Input from 'components/atoms/Input';
import TypeTag from 'components/atoms/TypeTag';
import Scrollbars from 'react-custom-scrollbars-2';
import { OpenModal, PortType, RootState, TagType, UserType } from 'types';
import { ActionsType } from 'types/system';
import { checkDifferent } from 'utils/formHandlers';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';
import { userTypeForPort } from 'utils/permission';

interface TagsTabProps {
    //unused props
    children?: string | JSX.Element | JSX.Element[];

    //unused props
    language: string;

    actions: ActionsType | undefined;

    //unused props
    errors?: any;
    tags: TagType[];
    openModelFunction: OpenModal;
    setTag: (id: number | string, type: string) => void;
    user: UserType;
    port: PortType;
    selectedTags: { id: number | string; usedForAll: boolean }[];
}

interface TagsTabState {
    tags: TagType[];
    searchTags: TagType[];
    searchQuery: string;
    selectedTags: { id: number | string; usedForAll: boolean }[];
}

class TagsTab extends Component<TagsTabProps, TagsTabState> {
    constructor(props: TagsTabProps) {
        super(props);
        this.state = {
            selectedTags: [],
            searchQuery: '',
            tags: props.tags
                .filter((tag: TagType) => {
                    if (userTypeForPort(props.user, props.port.id) === 'guest') {
                        return tag.type === 'private' && tag.user_id === props.user.id;
                    }
                    return (
                        tag.type === 'public' ||
                        tag.type === 'analysis' ||
                        (tag.type === 'private' && tag.user_id === props.user.id)
                    );
                })
                .sort((a, b) => {
                    // group: private & public = 1, analysis = 2
                    const aGroup = a.type === 'private' || a.type === 'public' ? 1 : 2;
                    const bGroup = b.type === 'private' || b.type === 'public' ? 1 : 2;

                    // 1️⃣ group sorting
                    if (aGroup !== bGroup) {
                        return aGroup - bGroup;
                    }

                    // 2️⃣ order sorting inside group (nulls last)
                    if (a.order == null && b.order == null) return 0;
                    if (a.order == null) return 1;
                    if (b.order == null) return -1;

                    return a.order - b.order;
                }),
            searchTags: props.tags
                .filter((tag: TagType) => {
                    if (userTypeForPort(props.user, props.port.id) === 'guest') {
                        return tag.type === 'private' && tag.user_id === props.user.id;
                    }
                    return (
                        tag.type === 'public' ||
                        tag.type === 'analysis' ||
                        (tag.type === 'private' && tag.user_id === props.user.id)
                    );
                })
                .sort((a, b) => {
                    // group: private & public = 1, analysis = 2
                    const aGroup = a.type === 'private' || a.type === 'public' ? 1 : 2;
                    const bGroup = b.type === 'private' || b.type === 'public' ? 1 : 2;

                    // 1️⃣ group sorting
                    if (aGroup !== bGroup) {
                        return aGroup - bGroup;
                    }

                    // 2️⃣ order sorting inside group (nulls last)
                    if (a.order == null && b.order == null) return 0;
                    if (a.order == null) return 1;
                    if (b.order == null) return -1;

                    return a.order - b.order;
                }),
        };
    }

    static getDerivedStateFromProps(props: TagsTabProps, state: TagsTabState) {
        // Check if tags or selectedTags have changed
        if (checkDifferent(props.tags, state.tags) || checkDifferent(props.selectedTags, state.selectedTags)) {
            const { tags } = props;
            const { searchQuery } = state;
            const lowerCaseQuery = searchQuery.toLocaleLowerCase();

            // Filter tags based on type and search query
            const filteredTags = tags.filter((tag: TagType) => {
                const matchesQuery = searchQuery.length === 0 || tag.label.toLocaleLowerCase().includes(lowerCaseQuery);

                if (userTypeForPort(props.user, props.port.id) === 'guest') {
                    return tag.type === 'private' && tag.user_id === props.user.id && matchesQuery;
                }

                return (
                    (tag.type === 'public' ||
                        tag.type === 'analysis' ||
                        (tag.type === 'private' && tag.user_id === props.user.id)) &&
                    matchesQuery
                );
            });

            return {
                tags,
                selectedTags: props.selectedTags,
                searchTags: filteredTags,
            };
        }

        return null;
    }

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.props.actions?.onCancel();
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    addNewTag = () => {
        this.props.openModelFunction(
            'usercreateTagModel',
            0,
            'medium',
            {},
            {
                onCancel: () => {
                    this.props.openModelFunction('usercreateTagModel');
                },
                onConfirm: () => {
                    //console.log('saved');
                },
            },
            true
        );
    };

    search = (name: string, value: string) => {
        const lowerCaseValue = value.toLocaleLowerCase();

        this.setState({
            searchQuery: value,
            searchTags: this.state.tags.filter((tag: TagType) => {
                const matchesQuery =
                    lowerCaseValue.length === 0 || tag.label.toLocaleLowerCase().includes(lowerCaseValue);

                if (userTypeForPort(this.props.user, this.props.port.id) === 'guest') {
                    return tag.type === 'private' && tag.user_id === this.props.user.id && matchesQuery;
                }

                return (
                    (tag.type === 'public' ||
                        tag.type === 'analysis' ||
                        (tag.type === 'private' && tag.user_id === this.props.user.id)) &&
                    matchesQuery
                );
            }),
        });
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className=" w-full rounded">
                    <div className="flex items-start justify-between mt-5">
                        <div>{translate('Select the user tag(s) you would like to add to the selected transits')}</div>
                        <div>
                            <Input
                                onChange={this.search}
                                placeholder={translate('Enter tag name')}
                                name="search"
                                className="px-4 py-1 w-32 rounded-md text-md text-bold   border border-gray-400"
                            ></Input>
                        </div>
                    </div>
                    <Scrollbars
                        renderThumbHorizontal={() => <div style={{ display: 'none' }} />}
                        renderTrackHorizontal={() => <div style={{ display: 'none' }} />}
                        style={{
                            height: 200,
                            maxHeight: 400,
                        }}
                    >
                        <div className="flex gap-2 mt-5">
                            <div className=" mt-5">
                                {this.state.searchTags.map((tag: TagType) => {
                                    return (
                                        <TypeTag
                                            className={twclsx(
                                                this.state.selectedTags.some(
                                                    (selectedTag) =>
                                                        ((this.props.port.backEnd?.version === 2 &&
                                                            selectedTag.id === tag.key) ||
                                                            selectedTag.id === tag.id) &&
                                                        !selectedTag.usedForAll
                                                ) && 'partly-used',
                                                `mt-1`,
                                                `mr-1`
                                            )}
                                            active={this.state.selectedTags.some(
                                                (selectedTag) =>
                                                    (this.props.port.backEnd?.version === 2 &&
                                                        selectedTag.id === tag.key) ||
                                                    selectedTag.id === tag.id
                                            )}
                                            key={tag.id}
                                            type={tag.type}
                                            onClick={() => {
                                                this.props.setTag(
                                                    //@ts-ignore
                                                    this.props.port.backEnd?.version === 2 ? tag.key : tag.id,
                                                    tag.type
                                                );
                                            }}
                                        >
                                            {tag.label}
                                        </TypeTag>
                                    );
                                })}
                            </div>
                        </div>
                    </Scrollbars>
                    <div className="mt-4 text-primary-link">
                        <div onClick={this.addNewTag} className="cursor-pointer">
                            {translate('Add another tag')}
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    user: state.user.user,
    language: state.user.user.language,
    tags: state.port.tags,
    port: state.port.port,
    openModelFunction: state.layout.openModelFunction,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(TagsTab);
